import { WallboxConfigurationUpdateDto, WallboxOcppDto, WallboxType } from 'src/Libs/wallboxes/dto'
import {
  convertChargeStrategyFormToDto,
  formToChargeStrategyConfiguration,
  formToWeekday,
} from 'src/Libs/wallboxes/transformers/weekday-form.transformer'
import { UNIT_FACTOR_WATT_TO_WATTHOUR } from 'src/Configuration/defaults'
import { isVersionGreaterThanOrEqualTo } from '../../utils'
import { StorageSoftwareVersion } from '../../types'
import { WallboxSettingsFormModel } from '../../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import { WallboxStandardConfigurationDto } from '../../dto/wallbox-configuration-update.dto'
import { AuthTypes } from '../../dto/wallbox-ocpp.dto'

/**
 *  Transforms the 'wallbox settings' form data to dto for updating the wallbox configuration
 */
const settingsFormToDtoTransformer = (
  wallboxType: WallboxType,
  formValues: WallboxSettingsFormModel,
  softwareVersion?: string
): WallboxConfigurationUpdateDto => {
  const isCombinedChargeStrategy = softwareVersion ? isVersionGreaterThanOrEqualTo(softwareVersion, StorageSoftwareVersion.X_2024_04) : false

  const model = {
    authRequired: formValues.authRequired,
    chargeStopHysteresis: formValues.chargeStopHysteresis,
    deviceName: formValues.deviceName,
    dhcpEnabled: formValues.dhcpEnabled,
    keyLockMode: formValues.keyLockMode,
    maxChargeCurrent: Number(formValues.maxChargeCurrent),
    minChargeCurrent: Number(formValues.minChargeCurrent),
    rfidReaderEnabled: formValues.rfidReaderEnabled,
    sunModeActive: formValues.sunModeActive,
    upperCurrentLimit: formValues.upperCurrentLimit,
    cableLock: formValues.cableLock,
    ...(wallboxType !== WallboxType.EDSN && {
      autoPhaseSwitchEnabled: formValues.autoPhaseSwitchEnabled,
      numberPhases: formValues.numberPhases,
      ledIntensity: formValues.ledIntensity === undefined ? undefined : Number(formValues.ledIntensity),
    }),
  } as WallboxStandardConfigurationDto

  if (wallboxType === WallboxType.Bidi) {
    model.diagBatCapacity = formValues.diagBatCapacity * UNIT_FACTOR_WATT_TO_WATTHOUR
    model.bidiModeActive = formValues.bidiModeActive
  }

  if (wallboxType === WallboxType.EDSN) {
    model.maxChargePower = Number(formValues.maxChargePower)
    model.minChargePower = Number(formValues.minChargePower)
    model.maxDischargePower = Number(formValues.maxDischargePower)
  }

  /** activeChargeStrategy, parameterList are only in software version < X_2004_04 */
  if (isCombinedChargeStrategy && formValues?.chargeStrategy && formValues?.chargeStrategyConfiguration) {
    model.chargeStrategy = convertChargeStrategyFormToDto(formValues.chargeStrategy, isCombinedChargeStrategy)
    model.chargeStrategyConfiguration = formValues.chargeStrategyConfiguration.map(configuration =>
      formToChargeStrategyConfiguration(configuration, wallboxType)
    )
  } else {
    model.activeChargeStrategy = formValues.activeChargeStrategy
    model.parameterList = formValues.weekdays?.map(formToWeekday)
  }

  if (wallboxType === WallboxType.Efy && formValues.ocppId) {
    const ocppDto: WallboxOcppDto = {
      enabled: formValues.ocppEnabled ?? false,
      id: formValues.ocppId,
      serverAddress: formValues.ocppServerAddress ?? '',
      authType: formValues.ocppAuthType ? AuthTypes.Enabled : AuthTypes.Disabled,
      allowLocalCharge: formValues.ocppAllowLocalCharge ?? false,
      password: formValues.ocppPassword || undefined, // NOSONAR - instead of empty string undefined should be send, thus || is correct here.
    }

    return {
      standardConfiguration: model,
      ocppConfiguration: ocppDto,
    }
  }

  return {
    standardConfiguration: model,
  }
}
export default settingsFormToDtoTransformer
